import { useState } from 'react';

import { Sparkles } from 'lucide-react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore posthog is in the parent dir
import { usePostHog } from 'posthog-js/react';

import { ALL_BUILT_MESSAGE } from 'components/Kit/BuiltLoads/constants';
import { ALL_HANDLED_MESSAGE } from 'components/Kit/HandledQuotes/constants';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { getReplyPointerText } from 'pages/QuoteView/Quoting/RequestQuickQuote/BatchLTL/utils/batchLTLSummary';
import { BatchLTLReplySummary } from 'types/BatchLTLReply';
import { Maybe } from 'types/UtilityTypes';

import {
  KitSelectionVariant,
  getKitBatchLTLMessage,
  getKitLoadFormalMessage,
  getKitQuoteSelectionTrailingCopy,
  joinKitFoundParts,
} from '../utils/kitMessage';
import {
  KitInfoHoverTarget,
  captureKitInfoHoverOnOpen,
  getPosthogCapture,
} from '../utils/posthogEvents';

type MessageBubbleProps = {
  foundParts: string[];
  hasKitSuggestions: boolean;
  carrierQuoteTabLabel: string;
  // Quote-view: auto-selects a suggestion into the form; Load-view does not.
  autoSelectsSuggestion?: boolean;
  showingHandledQuotes?: boolean;
  handledQuoteCount?: number;
  selectedHandledQuoteCount?: number;
  manualSelectionMessage?: string;
  // Formal-message selection shape (Quote auto-select / Load CTA).
  selectionVariant?: KitSelectionVariant;
  // When set, show empty-state copy for the current view context.
  emptyStateType?: Maybe<KitEmptyStateType>;
  // Load view: reopen thread loads after the user closed all tabs.
  associatedLoadsReopen?: Maybe<AssociatedLoadsReopen>;
  // Shipments in the Batch LTL panel, for the BatchLTL variant's copy.
  batchLTLShipmentCount?: number;
  // The Batch LTL panel's last reply, for the BatchLTLReplied variant's copy.
  batchLTLReplySummary?: Maybe<BatchLTLReplySummary>;
};

// Message bubble from Kit informing users about email suggestions for current thread
export function MessageBubble({
  foundParts,
  hasKitSuggestions,
  carrierQuoteTabLabel,
  autoSelectsSuggestion = true,
  showingHandledQuotes = false,
  handledQuoteCount = 0,
  manualSelectionMessage,
  selectionVariant = KitSelectionVariant.Multiple,
  emptyStateType = null,
  associatedLoadsReopen = null,
  batchLTLShipmentCount = 0,
  batchLTLReplySummary = null,
}: MessageBubbleProps) {
  const foundList = joinKitFoundParts(foundParts);
  let messageContent;

  if (
    emptyStateType === KitEmptyStateType.AssociatedLoadsClosed &&
    associatedLoadsReopen
  ) {
    messageContent = (
      <AssociatedLoadsClosedEmptyState {...associatedLoadsReopen} />
    );
  } else if (
    selectionVariant === KitSelectionVariant.BatchLTLReplied &&
    batchLTLReplySummary
  ) {
    messageContent = (
      <Typography
        as='span'
        variant='body-xs'
        weight='medium'
        className='text-neutral-800 block'
      >
        {getReplyPointerText(
          batchLTLReplySummary.action,
          batchLTLReplySummary.remainingCount
        )}
      </Typography>
    );
  } else if (
    emptyStateType &&
    emptyStateType !== KitEmptyStateType.AssociatedLoadsClosed
  ) {
    messageContent = (
      <Typography
        as='span'
        variant='body-xs'
        weight='medium'
        className='text-neutral-800 block'
      >
        {EMPTY_STATE_COPY[emptyStateType]}
      </Typography>
    );
  } else if (showingHandledQuotes) {
    messageContent = (
      <Typography
        as='span'
        variant='body-xs'
        weight='medium'
        className='text-neutral-800 block'
      >
        Hey, looking to revisit a quote? I found {handledQuoteCount} lane
        {handledQuoteCount === 1 ? '' : 's'} already <HandledHint /> in this
        thread, just select the ones you want to take another look at.
      </Typography>
    );
  } else if (
    hasKitSuggestions &&
    autoSelectsSuggestion &&
    selectionVariant === KitSelectionVariant.BatchLTL
  ) {
    messageContent = (
      <Typography
        as='span'
        variant='body-xs'
        weight='medium'
        className='text-neutral-800 block'
      >
        {getKitBatchLTLMessage(batchLTLShipmentCount)}
      </Typography>
    );
  } else if (hasKitSuggestions && autoSelectsSuggestion) {
    messageContent = (
      <Typography
        as='span'
        variant='body-xs'
        weight='medium'
        className='text-neutral-800 block'
      >
        {`Hey! I found ${foundList} and already `}
        <SelectedHint
          isMultipleSelected={selectionVariant === KitSelectionVariant.All}
        />
        {` ${getKitQuoteSelectionTrailingCopy(selectionVariant)}`}
      </Typography>
    );
  } else if (hasKitSuggestions) {
    messageContent = (
      <Typography
        as='span'
        variant='body-xs'
        weight='medium'
        className='text-neutral-800 block'
      >
        {manualSelectionMessage ??
          getKitLoadFormalMessage(foundList, selectionVariant)}
      </Typography>
    );
  } else {
    messageContent = (
      <Typography
        as='span'
        variant='body-xs'
        weight='medium'
        className='text-neutral-800 block'
      >{`Hey! I found ${foundList} for you - open the ${carrierQuoteTabLabel} tab to review AI-filled pricing.`}</Typography>
    );
  }

  return (
    <div className='w-full rounded-xl rounded-tl-sm border border-neutral-400 bg-neutral-100 px-3 py-2'>
      <Flex direction='col' gap='xs' className='w-full pl-2'>
        {messageContent}
      </Flex>
    </div>
  );
}

// Message bubble for empty state (no suggestions found)
export enum KitEmptyStateType {
  // Quote View
  NoSuggestionsForQuoteView = 'noSuggestionsForQuoteView',
  // Every quote request on the thread is handled; the handled groups show below
  AllQuotesHandled = 'allQuotesHandled',
  // Every load building card on the thread is built; the Built group shows below
  AllLoadsBuilt = 'allLoadsBuilt',
  // Load View
  NoAssociatedLoads = 'noAssociatedLoads',
  AssociatedLoadsClosed = 'associatedLoadsClosed',
  NoSuggestionsForLoad = 'noSuggestionsForLoad',
}

// Load-view empty states passed into Kit (excludes Quote empty).
export type KitLoadEmptyStateType = Exclude<
  KitEmptyStateType,
  | KitEmptyStateType.NoSuggestionsForQuoteView
  | KitEmptyStateType.AllQuotesHandled
  | KitEmptyStateType.AllLoadsBuilt
>;

// Intentional trade-off: one generic empty phrase for Quote + Load instead of
// context-aware language (email vs SharePoint vs portal vs Outlook / Gmail).
const NO_SUGGESTIONS_FOUND = 'No suggestions found.';

const EMPTY_STATE_COPY: Record<
  Exclude<KitEmptyStateType, KitEmptyStateType.AssociatedLoadsClosed>,
  string
> = {
  [KitEmptyStateType.NoSuggestionsForQuoteView]: NO_SUGGESTIONS_FOUND,
  [KitEmptyStateType.AllQuotesHandled]: ALL_HANDLED_MESSAGE,
  [KitEmptyStateType.AllLoadsBuilt]: ALL_BUILT_MESSAGE,
  [KitEmptyStateType.NoAssociatedLoads]:
    'No associated loads were found - you can still search by their IDs, customer, or stop information.',
  [KitEmptyStateType.NoSuggestionsForLoad]: NO_SUGGESTIONS_FOUND,
};

function SelectedHint({
  isMultipleSelected,
}: {
  isMultipleSelected?: boolean;
}) {
  const posthog = usePostHog();

  return (
    <Tooltip
      delayDuration={10}
      onOpenChange={(open) =>
        captureKitInfoHoverOnOpen(open, {
          capture: getPosthogCapture(posthog),
          infoTarget: KitInfoHoverTarget.SelectedKeyword,
        })
      }
    >
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className='cursor-help font-semibold text-accent-700 underline decoration-dotted decoration-accent-600 underline-offset-2'
        >
          selected
        </span>
      </TooltipTrigger>
      <TooltipContent
        side='bottom'
        className='max-w-[250px] bg-neutral-50 p-2 shadow-lg'
      >
        <Typography variant='body-xs' className='text-neutral-700'>
          {`When ${isMultipleSelected ? 'suggestions are' : 'a suggestion is'} `}
          <Typography
            as='span'
            variant='body-xs'
            weight='semibold'
            className='text-neutral-800'
          >
            selected
          </Typography>
          {`, Kit auto-fills ${isMultipleSelected ? 'their' : 'its'} information in the form for you, as highlighted by the `}
          <Sparkles
            className='inline h-3.5 w-3.5 stroke-accent-600!'
            aria-hidden
          />
          <Typography
            as='span'
            variant='body-xs'
            weight='semibold'
            className='text-accent-600'
          >
            {` AI-filled`}
          </Typography>
          {` hint.`}
        </Typography>
      </TooltipContent>
    </Tooltip>
  );
}

function HandledHint() {
  const posthog = usePostHog();

  return (
    <Tooltip
      delayDuration={10}
      onOpenChange={(open) =>
        captureKitInfoHoverOnOpen(open, {
          capture: getPosthogCapture(posthog),
          infoTarget: KitInfoHoverTarget.HandledKeyword,
        })
      }
    >
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className='cursor-help font-semibold text-accent-700 underline decoration-dotted decoration-accent-600 underline-offset-2'
        >
          handled
        </span>
      </TooltipTrigger>
      <TooltipContent
        side='bottom'
        className='max-w-[250px] bg-neutral-50 p-2 shadow-lg'
      >
        <Typography variant='body-xs' className='text-neutral-700'>
          Handled quotes are lanes that were already quoted, replied to, or
          skipped.
        </Typography>
      </TooltipContent>
    </Tooltip>
  );
}

// Allow users to reopen email-associated loads if they closed all tabs.
type AssociatedLoadsReopen = {
  freightTrackingIDs: string[];
  onOpenLoad: (freightTrackingID: string) => void;
  onOpenAll: () => void;
};

function AssociatedLoadsClosedEmptyState({
  freightTrackingIDs,
  onOpenLoad,
  onOpenAll,
}: AssociatedLoadsReopen) {
  const [open, setOpen] = useState(false);
  const count = freightTrackingIDs.length;
  const loadsLabel = count === 1 ? '1 load' : `${count} loads`;

  return (
    <Typography
      as='span'
      variant='body-xs'
      weight='medium'
      className='text-neutral-800 block'
    >
      {`Hey! I found `}
      <Tooltip open={open} onOpenChange={setOpen} delayDuration={10}>
        <TooltipTrigger asChild>
          <button
            type='button'
            className='cursor-pointer font-semibold text-accent-700 underline underline-offset-2'
            onMouseEnter={() => setOpen(true)}
          >
            {loadsLabel}
          </button>
        </TooltipTrigger>
        <TooltipContent
          side='bottom'
          sideOffset={6}
          className='max-w-sm max-h-[400px] overflow-y-auto border border-neutral-200 bg-neutral-50 p-2 shadow-lg'
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          <Flex direction='col' gap='sm' className='gap-y-1.5'>
            <Typography variant='body-xxs' className='text-neutral-600'>
              {`Click a load to open it or `}
              <button
                type='button'
                onClick={() => {
                  onOpenAll();
                  setOpen(false);
                }}
                className='cursor-pointer font-semibold text-accent-600'
              >
                here
              </button>
              {` to open them all.`}
            </Typography>
            <Flex direction='col' gap='xs' className='gap-y-0.5'>
              {freightTrackingIDs.map((id) => (
                <button
                  key={id}
                  type='button'
                  onClick={() => {
                    onOpenLoad(id);
                    setOpen(false);
                  }}
                  className='cursor-pointer text-left'
                >
                  <Typography
                    variant='body-xxs'
                    weight='semibold'
                    className='text-neutral-700 font-mono hover:text-accent-700'
                  >
                    {id}
                  </Typography>
                </button>
              ))}
            </Flex>
          </Flex>
        </TooltipContent>
      </Tooltip>
      {` associated with this email.`}
    </Typography>
  );
}
